import { DndContext, DragOverlay, PointerSensor, TouchSensor, pointerWithin, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import type {
    Announcements, CollisionDetection, DragEndEvent, DragMoveEvent, DragStartEvent, DraggableSyntheticListeners, Modifier, PointerSensorOptions,
    UniqueIdentifier,
} from '@dnd-kit/core';
import { TextInput, Tree } from '@orbitalwatch/design';
import type { ActionItem, DropPosition, ItemDefinition } from '@orbitalwatch/design';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { LockBadge } from './LockBadge';
import type { LockBadgeProps } from './LockBadge';
import { Icons } from './icons';
import { allIds, filterPageTree, findNode, isInSubtree, pathToNode, siblingsOf } from './pageTreeModel';
import type { PageTreeNode } from './pageTreeModel';

export type { PageTreeNode } from './pageTreeModel';

/** Lock of a node: the props of `LockBadge` (`own` / `inherited`, the locking page's title). */
export type PageTreeLock = Pick<LockBadgeProps, 'state' | 'by' | 'reason'>;

export interface PageTreeProps {
    nodes: PageTreeNode[];
    /** Header label (11 px eyebrow) and the tree's accessible name. Default "Pages". */
    title?: string;
    selectedId?: string;
    /** A row was chosen (context rows never call it). */
    onSelect?: (id: string, node: PageTreeNode) => void;
    /** Controlled expansion; omit it to let the tree expand the path to `selectedId` itself. */
    expandedIds?: string[];
    /** Called with the new list of expanded ids (controlled expansion). */
    onToggle?: (expandedIds: string[]) => void;
    /** Uncontrolled: every node starts expanded (the change-request tree). Default: only the path to `selectedId`. */
    defaultExpanded?: boolean;
    /** Text in the filter box (controlled with `onFilter`). */
    filter?: string;
    /**
     * The filter box changed (debounced 300 ms; cleared at once). With it the caller filters `nodes` (Ark's page search
     * is server-side); without it the tree filters by title itself, keeping ancestors of the matches.
     */
    onFilter?: (value: string) => void;
    /** Default "Find a page". `false` hides the filter box. */
    filterPlaceholder?: string | false;
    /** No drag and drop, no "+" / "⋯" actions (change requests, readers without write access). */
    readOnly?: boolean;
    /** Right-hand meta of a row (change-request kind dot, status pills). */
    flags?: (node: PageTreeNode) => ReactNode;
    /** Lock badge per node id (own / inherited). */
    locks?: Record<string, PageTreeLock | undefined>;
    /** "+": add a page under `parentId` (row) or at the root (header, `undefined`). */
    onAdd?: (parentId?: string) => void;
    /** "⋯" menu: Move up / Move down (the keyboard way to reorder). */
    onMove?: (id: string, direction: 'up' | 'down') => void;
    /** "⋯" menu: Rename / edit the page. */
    onEdit?: (id: string) => void;
    /** "⋯" menu: Delete (danger). */
    onDelete?: (id: string) => void;
    /** Drag a row onto the middle of another: make it a child of `targetId`. */
    onNest?: (draggedId: string, targetId: string) => void;
    /** Whether `draggedId` may become a child of `targetId` (no cycles). Default: not into its own subtree. */
    canNest?: (draggedId: string, targetId: string) => boolean;
    /** Drag a row onto the top / bottom edge of another: place it before / after `targetId` among its siblings. */
    onReorder?: (draggedId: string, targetId: string, position: 'before' | 'after') => void;
    /** Shown instead of the rows when there are none ("No pages yet", "No page matches"). */
    emptyState?: ReactNode;
    /** Footer under the tree ("+ New page"). */
    footer?: ReactNode;
    className?: string;
}

const FILTER_DEBOUNCE_MS = 300;

/* ---------- Drag and drop (dnd-kit) ----------
   The library Tree's own drag and drop is HTML5 drag events, which phones never fire; Ark keeps dnd-kit so pages also move
   by touch, and leaves the Tree's `dragDrop` unset so the two never fight. Each row's title is the grab area: the
   library renders it through `itemDefinition.render`, and that label registers the whole row (`.tree-item-content`) as
   both the draggable and the drop target. */

/** Top / bottom share of a row that places the dragged page before / after it; the middle nests (the pre-Meridian tree's zones). */
const EDGE_ZONE = 0.3;
/** Mouse and pen: a drag starts after 6 px of movement, so a click still selects the page. */
const POINTER_ACTIVATION = { distance: 6 };
/** Touch: press and hold 250 ms; moving more than 5 px before that scrolls instead, and a tap still selects. */
const TOUCH_ACTIVATION = { delay: 250, tolerance: 5 };

/** dnd-kit's PointerSensor for mouse and pen only: touch goes to the TouchSensor (press and hold), so swipes keep scrolling. */
class MouseAndPenSensor extends PointerSensor {
    static activators = [{
        eventName: 'onPointerDown' as const,
        handler: ({ nativeEvent: event }: ReactPointerEvent, { onActivation }: PointerSensorOptions) => {
            if (!event.isPrimary || event.button !== 0 || event.pointerType === 'touch') return false;
            onActivation?.({ event });
            return true;
        },
    }];
}

/** Where a drop on a row lands: edges reorder, the middle nests (only edges without `onNest`, only the middle without `onReorder`). */
function dropZone(offset: number, nest: boolean, reorder: boolean): DropPosition {
    if (!reorder) return 'into';
    if (!nest) return offset < 0.5 ? 'before' : 'after';
    if (offset < EDGE_ZONE) return 'before';
    if (offset > 1 - EDGE_ZONE) return 'after';
    return 'into';
}

function eventPoint(event: Event): { x: number; y: number } | null {
    if ('touches' in event) {
        const touch = (event as TouchEvent).touches?.[0] ?? (event as TouchEvent).changedTouches?.[0];
        return touch ? { x: touch.clientX, y: touch.clientY } : null;
    }
    return 'clientX' in event ? { x: (event as MouseEvent).clientX, y: (event as MouseEvent).clientY } : null;
}

/** Pins the ghost's corner to the pointer (the CSS then sets it below-right of the cursor, or above the finger). */
const ghostAtPointer: Modifier = ({ transform, activatorEvent, draggingNodeRect }) => {
    const start = activatorEvent ? eventPoint(activatorEvent) : null;
    if (!start || !draggingNodeRect) return transform;
    return { ...transform, x: transform.x + start.x - draggingNodeRect.left, y: transform.y + start.y - draggingNodeRect.top };
};

type Refusal = 'self' | 'subtree' | 'nest';
/** What a row shows while a page is dragged over it: the accepted drop, or refused. */
type RowDrop = DropPosition | 'refused';

interface DragView {
    activeId?: string;
    overId?: string;
    drop?: RowDrop;
}

const DragViewContext = createContext<DragView>({});

/** The ghost's short line and the full sentence for screen readers. */
function describeDrop(dragged: PageTreeNode, target: PageTreeNode, position: DropPosition, refusal?: Refusal): { hint: string; full: string } {
    if (refusal === 'subtree') return { hint: 'Cannot move into its own subpages', full: `“${dragged.title}” cannot move into its own subpages` };
    if (refusal) return { hint: `Cannot move under “${target.title}”`, full: `“${dragged.title}” cannot move under “${target.title}”` };
    if (position === 'into') return { hint: `Move under “${target.title}”`, full: `Move “${dragged.title}” under “${target.title}”` };
    return { hint: `Place ${position} “${target.title}”`, full: `Place “${dragged.title}” ${position} “${target.title}”` };
}

interface RowGrab {
    ref: (label: HTMLSpanElement | null) => void;
    listeners: DraggableSyntheticListeners;
    drop?: RowDrop;
    dragging: boolean;
}

/** A row's title (and lock); with `grab`, the title is what picks the row up. */
function RowLabel(props: { node: PageTreeNode; lock?: PageTreeLock; grab?: RowGrab }) {
    const { node, lock, grab } = props;
    const css = ['ark-tree-label', node.context ? 'is-context' : '', node.struck ? 'is-struck' : '', grab?.listeners ? 'is-draggable' : ''];
    return (
        <span
            ref={grab?.ref}
            {...grab?.listeners}
            className={css.filter(Boolean).join(' ')}
            title={node.context ? `${node.title} (unchanged)` : node.title}
            data-drop={grab?.drop}
            data-dragging={grab?.dragging ? 'true' : undefined}
        >
            <span className="ark-tree-title">{node.title}</span>
            {lock ? <LockBadge state={lock.state} by={lock.by} reason={lock.reason} /> : null}
        </span>
    );
}

/** A row title that picks the row up (mouse, pen, touch) and makes the row a drop target. */
function DraggableRowLabel(props: { node: PageTreeNode; lock?: PageTreeLock; disabled: boolean }) {
    const { node, lock, disabled } = props;
    const view = useContext(DragViewContext);
    const { setNodeRef: setDragNode, listeners, isDragging } = useDraggable({ id: node.id, disabled: disabled || !!node.context });
    const { setNodeRef: setDropNode } = useDroppable({ id: node.id, disabled });
    // the library renders this label inside the row: the row is what moves and what is measured for the drop zones
    const ref = useCallback((label: HTMLSpanElement | null) => {
        const row = label?.closest<HTMLElement>('.tree-item-content') ?? null;
        setDragNode(row);
        setDropNode(row);
    }, [setDragNode, setDropNode]);
    const drop = view.activeId !== undefined && view.overId === node.id ? view.drop : undefined;
    return <RowLabel node={node} lock={lock} grab={{ ref, listeners, drop, dragging: isDragging }} />;
}

/**
 * The page tree of a space (and, read-only, the changed pages of a change request): the library `Tree` (Meridian rows,
 * chevrons, keyboard, roving focus) with a library `TextInput` filter above it, Ark's `LockBadge` after the title, "+"
 * (add child) as the row's main action and Move up / Move down / Rename / Delete in its "⋯" menu. Rows move with dnd-kit
 * (mouse, pen and touch): drop on the top / bottom 30 % of a row to place the page before / after it, on the middle to nest
 * it; a ghost row follows the pointer and says what the drop does, or why it is refused.
 */
export function PageTree(props: PageTreeProps) {
    const { nodes, onFilter, readOnly, locks, flags } = props;
    const title = props.title ?? 'Pages';

    // Filter box: local text, reported debounced (server search) or applied here (local filter).
    const [text, setText] = useState(props.filter ?? '');
    // a new `filter` from the caller replaces the text (adjusted while rendering, React's "previous prop" pattern)
    const [filterProp, setFilterProp] = useState(props.filter);
    if (props.filter !== filterProp) {
        setFilterProp(props.filter);
        if (props.filter !== undefined && props.filter !== text) setText(props.filter);
    }
    const lastReported = useRef(props.filter ?? '');
    const controlledFilter = props.filter;
    useEffect(() => {
        if (!onFilter || text === lastReported.current || text === controlledFilter) return;
        const report = () => {
            lastReported.current = text;
            onFilter(text);
        };
        if (text === '') {
            report();
            return;
        }
        const timer = window.setTimeout(report, FILTER_DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [text, onFilter, controlledFilter]);

    const items = useMemo(() => (onFilter ? nodes : filterPageTree(nodes, text)), [nodes, onFilter, text]);

    // Expansion: controlled, or the path to the selection (plus whatever the user opened), or everything.
    const [ownExpanded, setOwnExpanded] = useState<string[]>(() => (props.defaultExpanded ? allIds(nodes) : []));
    // new nodes or a new selection open the path to the selected page (and, with `defaultExpanded`, every new parent);
    // adjusted while rendering so the first paint is already expanded
    const [seen, setSeen] = useState<{ nodes: PageTreeNode[]; selectedId?: string }>({ nodes: [], selectedId: undefined });
    if (!props.expandedIds && (seen.nodes !== nodes || seen.selectedId !== props.selectedId)) {
        setSeen({ nodes, selectedId: props.selectedId });
        const wanted = [
            ...(props.selectedId ? pathToNode(nodes, props.selectedId) ?? [] : []),
            ...(props.defaultExpanded ? allIds(nodes) : []),
        ];
        const missing = wanted.filter(id => !ownExpanded.includes(id));
        if (missing.length) setOwnExpanded(Array.from(new Set([...ownExpanded, ...missing])));
    }
    // a filtered tree shows every match: expand everything while text is typed
    const filtering = text.trim() !== '';
    const expandedIds = props.expandedIds ?? (filtering ? allIds(items) : ownExpanded);
    const onExpandedIdsChanged = useCallback((ids: string[]) => {
        if (props.onToggle) props.onToggle(ids);
        if (!props.expandedIds) setOwnExpanded(ids);
    }, [props]);

    // Drag and drop: on whenever a drop does something; rows stay put while the list is filtered (a partial tree).
    const { onNest, onReorder, canNest } = props;
    const dndOn = !readOnly && !!(onNest || onReorder);
    const dragDisabled = filtering;

    const { onAdd, onMove, onEdit, onDelete, onSelect } = props;
    const hasMenu = !readOnly && !!(onAdd || onMove || onEdit || onDelete);

    const itemDefinition = useMemo((): ItemDefinition<PageTreeNode> => ({
        id: node => node.id,
        children: node => (node.children && node.children.length ? node.children : undefined),
        // every row is a page (boards: the page glyph; the chevron says it has subpages)
        icon: () => <Icons.pages size={16} />,
        render: node => (dndOn
            ? <DraggableRowLabel node={node} lock={locks?.[node.id]} disabled={dragDisabled} />
            : <RowLabel node={node} lock={locks?.[node.id]} />),
        kind: flags ? node => flags(node) : undefined,
        onItemClicked: node => {
            if (!node.context) onSelect?.(node.id, node);
        },
        actions: hasMenu ? node => {
            const actions: ActionItem<PageTreeNode>[] = [];
            const siblings = siblingsOf(nodes, node.id) ?? [];
            const index = siblings.findIndex(sibling => sibling.id === node.id);
            // the library shows the first action as the row's own button ("+"), the rest in the "⋯" menu
            if (onAdd) actions.push({ label: `Add a page under ${node.title}`, icon: <Icons.plus size={14} />, onClick: item => onAdd(item.id) });
            if (onAdd) actions.push({ label: 'Add child page', icon: <Icons.plus size={14} />, onClick: item => onAdd(item.id) });
            if (onMove) {
                actions.push({ label: 'Move up', icon: <Icons.arrowUp size={14} />, onClick: item => onMove(item.id, 'up'), isDisabled: index <= 0 });
                actions.push({ label: 'Move down', icon: <Icons.arrowDown size={14} />, onClick: item => onMove(item.id, 'down'), isDisabled: index === -1 || index >= siblings.length - 1 });
            }
            if (onEdit) actions.push({ label: 'Rename', icon: <Icons.edit size={14} />, onClick: item => onEdit(item.id) });
            if (onDelete) actions.push({ label: 'Delete', icon: <Icons.trash size={14} />, onClick: item => onDelete(item.id) });
            return actions;
        } : undefined,
    }), [locks, flags, onSelect, hasMenu, nodes, onAdd, onMove, onEdit, onDelete, dndOn, dragDisabled]);

    const sensors = useSensors(
        useSensor(MouseAndPenSensor, { activationConstraint: POINTER_ACTIVATION }),
        useSensor(TouchSensor, { activationConstraint: TOUCH_ACTIVATION }),
    );

    // the row under the pointer and the zone of that row the pointer is in (top / middle / bottom)
    const collisionDetection = useCallback<CollisionDetection>(args => {
        const [hit] = pointerWithin(args);
        const pointer = args.pointerCoordinates;
        const rect = hit ? args.droppableRects.get(hit.id) : undefined;
        if (!hit || !pointer || !rect || rect.height === 0) return [];
        const position = dropZone((pointer.y - rect.top) / rect.height, !!onNest, !!onReorder);
        return [{ id: hit.id, data: { ...hit.data, position } }];
    }, [onNest, onReorder]);

    const refusalOf = useCallback((draggedId: string, targetId: string, position: DropPosition): Refusal | undefined => {
        if (draggedId === targetId) return 'self';
        if (isInSubtree(nodes, draggedId, targetId)) return 'subtree';
        if (position === 'into' && (!onNest || (canNest && !canNest(draggedId, targetId)))) return 'nest';
        return undefined;
    }, [nodes, onNest, canNest]);

    const [drag, setDrag] = useState<{ activeId?: string; touch: boolean; overId?: string; position?: DropPosition }>({ touch: false });
    const activeNode = drag.activeId ? findNode(nodes, drag.activeId) : undefined;
    const overNode = drag.overId ? findNode(nodes, drag.overId) : undefined;
    const refusal = activeNode && overNode && drag.position ? refusalOf(activeNode.id, overNode.id, drag.position) : undefined;
    // over its own row a drag shows nothing yet
    const verdict = activeNode && overNode && drag.position && refusal !== 'self' ? describeDrop(activeNode, overNode, drag.position, refusal) : undefined;
    const rowDrop: RowDrop | undefined = verdict ? (refusal ? 'refused' : drag.position) : undefined;
    const dragView = useMemo((): DragView => ({ activeId: drag.activeId, overId: drag.overId, drop: rowDrop }), [drag.activeId, drag.overId, rowDrop]);

    const handleDragStart = useCallback((event: DragStartEvent) => {
        const touch = typeof TouchEvent !== 'undefined' && event.activatorEvent instanceof TouchEvent;
        setDrag({ activeId: String(event.active.id), touch });
    }, []);

    const handleDragMove = useCallback((event: DragMoveEvent) => {
        const [hit] = event.collisions ?? [];
        const overId = hit ? String(hit.id) : undefined;
        const position = (hit?.data as { position?: DropPosition } | undefined)?.position;
        setDrag(previous => (previous.overId === overId && previous.position === position ? previous : { ...previous, overId, position }));
    }, []);

    // what the screen reader hears when the page is let go (read by the announcement right after onDragEnd)
    const outcome = useRef('');
    const handleDragEnd = useCallback((event: DragEndEvent) => {
        setDrag({ touch: false });
        const draggedId = String(event.active.id);
        const dragged = findNode(nodes, draggedId);
        const [hit] = event.collisions ?? [];
        const position = (hit?.data as { position?: DropPosition } | undefined)?.position;
        const target = hit ? findNode(nodes, String(hit.id)) : undefined;
        outcome.current = dragged ? `“${dragged.title}” was not moved.` : '';
        if (!dragged || !target || !position || refusalOf(dragged.id, target.id, position)) return;
        if (position === 'into') {
            outcome.current = `“${dragged.title}” moved under “${target.title}”.`;
            onNest?.(dragged.id, target.id);
        } else {
            outcome.current = `“${dragged.title}” placed ${position} “${target.title}”.`;
            onReorder?.(dragged.id, target.id, position);
        }
    }, [nodes, refusalOf, onNest, onReorder]);

    const handleDragCancel = useCallback(() => setDrag({ touch: false }), []);

    const accessibility = useMemo(() => {
        const titleOf = (id: UniqueIdentifier) => findNode(nodes, String(id))?.title ?? 'the page';
        const announcements: Announcements = {
            onDragStart: ({ active }) => `Picked up “${titleOf(active.id)}”.`,
            // the drop zone under the pointer is announced by the tree's own live region below
            onDragOver: () => undefined,
            onDragEnd: () => outcome.current || undefined,
            onDragCancel: ({ active }) => `Moving “${titleOf(active.id)}” was cancelled.`,
        };
        return {
            announcements,
            screenReaderInstructions: {
                draggable: 'Drag a page with the mouse, or press and hold it on a touch screen, then drop it on the top or bottom edge of another page to place it before or after that page, or on its middle to move it under it. With the keyboard, use Move up and Move down in the page\'s actions menu.',
            },
        };
    }, [nodes]);

    const headerActions = !readOnly && onAdd ? [{ label: 'Add a page', icon: <Icons.plus size={14} />, onClick: () => onAdd(undefined) }] : undefined;
    const empty = props.emptyState ?? (filtering ? <p className="ark-muted">No page matches “{text.trim()}”.</p> : <p className="ark-muted">No pages yet.</p>);

    const tree = (
        <Tree<PageTreeNode>
            items={items}
            itemDefinition={itemDefinition}
            header={{ label: title, actions: headerActions }}
            ariaLabel={title}
            selectedId={props.selectedId}
            expandedIds={expandedIds}
            onExpandedIdsChanged={onExpandedIdsChanged}
            emptyState={empty}
            density="compact"
            className="ark-tree"
        />
    );

    // dnd-kit measures the overlay's only child: a full-size anchor keeps that measure on the row's box, the ghost sits off it
    const ghost = activeNode ? (
        <div className="ark-tree-ghost-anchor">
            <div className={['ark-tree-ghost', drag.touch ? 'is-touch' : '', rowDrop === 'refused' ? 'is-refused' : rowDrop ? 'is-accepted' : ''].filter(Boolean).join(' ')} aria-hidden="true">
                <span className="ark-tree-ghost-row">
                    <Icons.pages size={16} />
                    <span className="ark-tree-ghost-title">{activeNode.title}</span>
                </span>
                {verdict ? <span className="ark-tree-ghost-hint">{verdict.hint}</span> : null}
            </div>
        </div>
    ) : null;

    return (
        <div className={['ark-page-tree', drag.activeId ? 'is-dragging' : '', rowDrop === 'refused' ? 'is-refused' : '', props.className].filter(Boolean).join(' ')}>
            {props.filterPlaceholder !== false ? (
                <div className="ark-page-tree-filter">
                    <TextInput
                        type="search"
                        value={text}
                        placeholder={props.filterPlaceholder || 'Find a page'}
                        aria-label={props.filterPlaceholder || 'Find a page'}
                        icon={<Icons.search size={16} />}
                        onChange={(value: string) => setText(value ?? '')}
                        onKeyDown={event => {
                            if (event.key === 'Escape' && text) {
                                event.stopPropagation();
                                setText('');
                            }
                        }}
                    />
                </div>
            ) : null}
            {dndOn ? (
                <DndContext
                    sensors={sensors}
                    collisionDetection={collisionDetection}
                    accessibility={accessibility}
                    onDragStart={handleDragStart}
                    onDragMove={handleDragMove}
                    onDragEnd={handleDragEnd}
                    onDragCancel={handleDragCancel}
                >
                    <DragViewContext.Provider value={dragView}>{tree}</DragViewContext.Provider>
                    {/* in <body>: a transformed drawer around the tree would otherwise move the fixed ghost */}
                    {createPortal(<DragOverlay className="ark-tree-ghost-layer" dropAnimation={null} modifiers={[ghostAtPointer]}>{ghost}</DragOverlay>, document.body)}
                </DndContext>
            ) : tree}
            {dndOn ? <div className="ow-sr-only" role="status" aria-live="polite">{verdict?.full ?? ''}</div> : null}
            {props.footer ? <div className="ark-page-tree-foot">{props.footer}</div> : null}
        </div>
    );
}
